import axiosInstance from "../config/axiosApi/axiosApi.jsx";
import APIs from "../Constants/apis.js";

/**
 * Remove security-sensitive fields that must not live in Redux / localStorage.
 * Full CompSecCode remains available only via GET /api/Company/Profile on Account Info.
 */
export function sanitizeProfileForStorage(profile) {
  if (!profile || typeof profile !== "object") return profile;
  const {
    compSecCode: _c1,
    CompSecCode: _c2,
    compRecoveryEmail: _r1,
    CompRecoveryEmail: _r2,
    compRecoveryPhoneNumber: _r3,
    CompRecoveryPhoneNumber: _r4,
    pendingCompEmail: _p1,
    PendingCompEmail: _p2,
    compEmailChangeExpiresUtc: _e1,
    CompEmailChangeExpiresUtc: _e2,
    ...safe
  } = profile;
  return safe;
}

/**
 * Service to handle user profile and settings data fetching
 */
export const userDataService = {
  /**
   * Fetch slim bootstrap profile (no CompSecCode). Used by login / refresh / hooks.
   * @returns {Promise<Object>} Bootstrap profile data
   */
  async fetchUserProfile(options = {}) {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_BOOTSTRAP_PROFILE, {
        skipUnauthorizedRedirect: !!options.skipUnauthorizedRedirect,
      });
      return sanitizeProfileForStorage(response.data.data);
    } catch (error) {
      if (!options.silent) {
        console.error("Error fetching user profile:", error);
      }
      throw error;
    }
  },

  /**
   * Fetch user settings data
   * @returns {Promise<Object>} Settings data
   */
  async fetchUserSettings(options = {}) {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_SETTINGS, {
        skipUnauthorizedRedirect: !!options.skipUnauthorizedRedirect,
      });
      return response.data.data;
    } catch (error) {
      if (!options.silent) {
        console.error("Error fetching user settings:", error);
      }
      throw error;
    }
  },

  /**
   * Fetch both bootstrap profile and settings data
   * @returns {Promise<Object>} Object containing profileData and settingsData
   */
  async fetchUserData(options = {}) {
    try {
      if (options.allowPartial) {
        const [profileResult, settingsResult] = await Promise.allSettled([
          this.fetchUserProfile(options),
          this.fetchUserSettings(options),
        ]);

        return {
          profileData:
            profileResult.status === "fulfilled"
              ? sanitizeProfileForStorage(profileResult.value)
              : null,
          settingsData: settingsResult.status === "fulfilled" ? settingsResult.value : null,
          lastDataRefresh: new Date().toISOString(),
        };
      }

      const [profileResponse, settingsResponse] = await Promise.all([
        this.fetchUserProfile(options),
        this.fetchUserSettings(options),
      ]);

      return {
        profileData: sanitizeProfileForStorage(profileResponse),
        settingsData: settingsResponse,
        lastDataRefresh: new Date().toISOString(),
      };
    } catch (error) {
      console.error("Error fetching user data:", error);
      throw error;
    }
  },
};

export default userDataService;
